import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shared pure-render component for the Sill pricing surface.
 *
 * Takes a `PricingTableData` payload (plans, add-ons, free_sites) and
 * renders the public pricing table. No fetching, no routing, no
 * theming internals — the consuming app wraps this with its own
 * header / footer / theme provider. The same component ships on
 * `sill.so/pricing` and inside ops2026 as the staff preview, so the
 * two surfaces can never drift.
 *
 * Design tokens follow CLAUDE.md §4: Instrument Serif headings, Geist
 * body, JetBrains Mono kickers, 1px borders, 2–4px radii, lime as the
 * single accent. Colours come from the theme-tokens object passed in,
 * so dark + light both work.
 *
 * Copy discipline (positioning SKILL + VERIFIED_FACTS.md): nothing is
 * asserted beyond what the catalog data says. The "Agencies" card
 * carries a mailto / link only, no commercial claim.
 */
import { useMemo, useState } from 'react';
import { mono, sans, serif } from '../fonts.js';
const DIMENSION_LABELS = {
    page_loads_monthly: 'Page loads a month',
    orders_monthly: 'Agent orders a month',
    assistant_requests_monthly: 'Assistant requests a month',
    retention_days: 'Reporting history',
};
const FEATURE_LABELS = {
    blocking: 'Address and attack-campaign blocking',
    custom_attack_paths: 'Custom attack paths',
    slack_held_alerts: 'Held-order alerts in Slack',
    assistant_blocking: 'Assistant can create blocks',
    // Account-scope features aren't on site plans today but the labels
    // live here so a future ops decision can surface them.
    white_label: 'White-label reports',
    agency_portal: 'Agency Portal',
};
/** Site-dimensions a site plan may legitimately carry in the UI, in
 *  display order. Any other dimension is dropped — a stray row from
 *  the catalog never surfaces as a blank label. */
const SITE_DIMENSIONS_ORDER = [
    'page_loads_monthly',
    'orders_monthly',
    'assistant_requests_monthly',
    'retention_days',
];
/** Format a price's minor units into a human string using the row's
 *  currency. A $0 price always renders as "Free" to keep the Free
 *  tier clear regardless of what the catalog says the currency is. */
export function formatPrice(price) {
    if (price.amount_minor === 0)
        return 'Free';
    const currency = price.currency.toUpperCase();
    const major = price.amount_minor / 100;
    try {
        const fmt = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
            maximumFractionDigits: 2,
        });
        return fmt.format(major);
    }
    catch {
        // Unknown ISO code → degrade to a plain decimal + the code.
        return `${major.toFixed(Number.isInteger(major) ? 0 : 2)} ${currency}`;
    }
}
/** Format a limit value for display. `null` → "Unlimited";
 *  `retention_days` turns into a day/month/year phrase. */
export function formatLimit(limit) {
    if (limit.limit_value === null)
        return 'Unlimited';
    if (limit.dimension === 'retention_days') {
        const d = limit.limit_value;
        if (d >= 365) {
            const years = Math.round(d / 365);
            return years === 1 ? '1 year' : `${years} years`;
        }
        if (d >= 30) {
            const months = Math.round(d / 30);
            return months === 1 ? '1 month' : `${months} months`;
        }
        return d === 1 ? '1 day' : `${d} days`;
    }
    // Numeric limits render with locale-appropriate thousand separators.
    return limit.limit_value.toLocaleString('en-US');
}
function pickPrice(prices, interval) {
    return prices.find((p) => p.interval === interval);
}
/** True iff ANY plan or add-on in the data has a yearly price. The
 *  monthly/yearly toggle hides itself when this is false. */
export function hasYearly(data) {
    for (const plan of data.plans) {
        if (pickPrice(plan.prices, 'yearly') !== undefined)
            return true;
    }
    for (const addon of data.addons) {
        if (pickPrice(addon.prices, 'yearly') !== undefined)
            return true;
    }
    return false;
}
function badge(theme, kind) {
    const base = {
        ...mono,
        fontSize: 10,
        letterSpacing: 2,
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 2,
        border: `1px solid ${theme.border}`,
        color: theme.muted,
    };
    if (kind === 'draft') {
        return { ...base, color: theme.amberFg, borderColor: theme.amber };
    }
    return base;
}
function Section({ children, theme, id, }) {
    return (_jsx("section", { id: id, style: {
            background: theme.bg,
            color: theme.text,
            padding: '48px 20px',
        }, children: _jsx("div", { style: { maxWidth: 1120, margin: '0 auto' }, children: children }) }));
}
/**
 * The pricing surface. Pure render — no fetching, no routing. See
 * `PricingTableProps` for the fields it reads.
 */
export function PricingTable(props) {
    const { data, theme, showDraftBadges = false, agenciesHref = '/agencies', ctaHref = 'https://app.sill.so/sign-up' } = props;
    const yearlyAvailable = useMemo(() => hasYearly(data), [data]);
    const [interval, setInterval] = useState('monthly');
    // If the data loses yearly entirely, clamp.
    const effectiveInterval = yearlyAvailable ? interval : 'monthly';
    if (data.plans.length === 0 && data.addons.length === 0) {
        return (_jsxs(Section, { theme: theme, id: "pricing-empty", children: [_jsx("div", { style: { ...mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: theme.muted, marginBottom: 12 }, children: "Pricing" }), _jsx("h1", { style: {
                        ...serif,
                        fontSize: 'clamp(32px, 6vw, 48px)',
                        lineHeight: 1.05,
                        margin: 0,
                        letterSpacing: -1.5,
                        color: theme.text,
                    }, children: "Pricing is being finalised." }), _jsx("p", { style: { ...sans, fontSize: 16, color: theme.body, maxWidth: 640, marginTop: 16 }, children: "The plan details will be live here once the Sill catalog is published. In the meantime, Discovery is live and free: install Sill, see what AI assistants did on your site, and export signed records." })] }));
    }
    return (_jsxs(Section, { theme: theme, id: "pricing", children: [_jsx("div", { style: {
                    ...mono,
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: theme.muted,
                    marginBottom: 12,
                }, children: "Pricing" }), _jsx("h1", { style: {
                    ...serif,
                    fontSize: 'clamp(32px, 6vw, 48px)',
                    lineHeight: 1.05,
                    margin: 0,
                    letterSpacing: -1.5,
                    color: theme.text,
                }, children: "Plans priced per site." }), _jsx("p", { style: {
                    ...sans,
                    fontSize: 16,
                    color: theme.body,
                    maxWidth: 640,
                    marginTop: 12,
                    marginBottom: 24,
                }, children: data.free_sites !== null
                    ? `Every account gets ${data.free_sites} ${data.free_sites === 1 ? 'site' : 'sites'} on Free. Paid plans unlock protection and higher limits.`
                    : 'Every account starts on Free. Paid plans unlock protection and higher limits.' }), yearlyAvailable ? (_jsx("div", { role: "radiogroup", "aria-label": "Billing interval", style: {
                    display: 'inline-flex',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    marginBottom: 32,
                }, children: ['monthly', 'yearly'].map((it) => {
                    const active = effectiveInterval === it;
                    return (_jsx("button", { type: "button", role: "radio", "aria-checked": active, onClick: () => setInterval(it), style: {
                            ...sans,
                            background: active ? theme.lime : 'transparent',
                            color: active ? theme.ink : theme.text,
                            border: 'none',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: 13,
                            letterSpacing: 0.5,
                            minHeight: 36,
                        }, children: it === 'monthly' ? 'Monthly' : 'Yearly' }, it));
                }) })) : null, _jsxs("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 16,
                    alignItems: 'stretch',
                }, children: [data.plans.map((plan) => (_jsx(PlanCard, { plan: plan, theme: theme, interval: effectiveInterval, showDraftBadge: showDraftBadges, ctaHref: ctaHref }, plan.plan_id))), _jsx(AgenciesCard, { theme: theme, href: agenciesHref })] }), data.addons.length > 0 ? (_jsx(AddonsSection, { addons: data.addons, theme: theme, interval: effectiveInterval, showDraftBadges: showDraftBadges })) : null] }));
}
function PlanCard({ plan, theme, interval, showDraftBadge, ctaHref, }) {
    const price = pickPrice(plan.prices, interval) ?? pickPrice(plan.prices, 'monthly');
    // Site plans only ever carry site dimensions here; still filter.
    const limits = SITE_DIMENSIONS_ORDER
        .map((d) => plan.limits.find((l) => l.dimension === d))
        .filter((l) => l !== undefined);
    const featureLabels = plan.features
        .map((slug) => FEATURE_LABELS[slug])
        .filter((label) => typeof label === 'string');
    return (_jsxs("article", { "aria-labelledby": `plan-${plan.slug}-name`, style: {
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
        }, children: [_jsxs("header", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }, children: [_jsx("h2", { id: `plan-${plan.slug}-name`, style: {
                            ...serif,
                            fontSize: 24,
                            lineHeight: 1,
                            margin: 0,
                            color: theme.text,
                            letterSpacing: -0.5,
                        }, children: plan.name }), plan.is_default ? (_jsx("span", { style: badge(theme, 'default'), "aria-label": "Default plan", children: "Default" })) : null, showDraftBadge && plan.status === 'draft' ? (_jsx("span", { style: badge(theme, 'draft'), "aria-label": "Draft (not live)", children: "Draft" })) : null] }), plan.description !== null && plan.description.length > 0 ? (_jsx("p", { style: { ...sans, fontSize: 14, color: theme.body, margin: '0 0 16px' }, children: plan.description })) : null, _jsx("div", { style: { marginBottom: 20 }, children: price !== undefined ? (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                ...serif,
                                fontSize: 32,
                                lineHeight: 1,
                                color: theme.text,
                                letterSpacing: -1,
                            }, children: formatPrice(price) }), price.amount_minor > 0 ? (_jsxs("div", { style: { ...mono, fontSize: 11, color: theme.muted, marginTop: 4, letterSpacing: 1 }, children: ["per site \u00B7 ", interval === 'yearly' ? 'yearly' : 'monthly'] })) : null] })) : (_jsx("div", { style: { ...serif, fontSize: 24, color: theme.muted }, children: "Price pending." })) }), limits.length > 0 ? (_jsx("dl", { style: {
                    margin: '0 0 20px',
                    padding: '12px 0',
                    borderTop: `1px solid ${theme.borderSoft}`,
                    borderBottom: `1px solid ${theme.borderSoft}`,
                    display: 'grid',
                    rowGap: 6,
                }, children: limits.map((l) => (_jsxs("div", { style: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        ...sans,
                        fontSize: 13,
                    }, children: [_jsx("dt", { style: { color: theme.muted }, children: DIMENSION_LABELS[l.dimension] ?? l.dimension }), _jsx("dd", { style: { color: theme.text, margin: 0, textAlign: 'right' }, children: formatLimit(l) })] }, l.dimension))) })) : null, featureLabels.length > 0 ? (_jsx("ul", { style: {
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 20px',
                    display: 'grid',
                    rowGap: 6,
                }, children: featureLabels.map((label) => (_jsx("li", { style: { ...sans, fontSize: 13, color: theme.body }, children: label }, label))) })) : null, _jsx("div", { style: { marginTop: 'auto' }, children: _jsx("a", { href: ctaHref, style: {
                        ...sans,
                        display: 'inline-block',
                        background: plan.is_default ? 'transparent' : theme.lime,
                        color: plan.is_default ? theme.text : theme.ink,
                        padding: '10px 16px',
                        borderRadius: 2,
                        border: `1px solid ${plan.is_default ? theme.border : theme.lime}`,
                        textDecoration: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        minHeight: 44,
                        boxSizing: 'border-box',
                    }, children: price !== undefined && price.amount_minor === 0 ? 'Start free' : 'Get started' }) })] }));
}
function AgenciesCard({ theme, href }) {
    return (_jsxs("article", { "aria-labelledby": "plan-agencies-name", style: {
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
        }, children: [_jsx("header", { style: { marginBottom: 8 }, children: _jsx("h2", { id: "plan-agencies-name", style: {
                        ...serif,
                        fontSize: 24,
                        lineHeight: 1,
                        margin: 0,
                        color: theme.text,
                        letterSpacing: -0.5,
                    }, children: "Agencies" }) }), _jsx("p", { style: { ...sans, fontSize: 14, color: theme.body, margin: '0 0 16px' }, children: "Run many client sites from one account. Agency plans are set up by Sill." }), _jsx("div", { style: { marginTop: 'auto' }, children: _jsx("a", { href: href, style: {
                        ...sans,
                        display: 'inline-block',
                        background: 'transparent',
                        color: theme.text,
                        padding: '10px 16px',
                        borderRadius: 2,
                        border: `1px solid ${theme.border}`,
                        textDecoration: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        minHeight: 44,
                        boxSizing: 'border-box',
                    }, children: "Talk to us" }) })] }));
}
function AddonsSection({ addons, theme, interval, showDraftBadges, }) {
    return (_jsxs("div", { style: { marginTop: 48 }, children: [_jsx("div", { style: {
                    ...mono,
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: theme.muted,
                    marginBottom: 12,
                }, children: "Add-ons" }), _jsx("h2", { style: {
                    ...serif,
                    fontSize: 'clamp(24px, 4vw, 32px)',
                    lineHeight: 1.1,
                    margin: 0,
                    letterSpacing: -1,
                    color: theme.text,
                }, children: "Extra capacity on any plan." }), _jsx("div", { style: {
                    marginTop: 24,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 16,
                }, children: addons.map((a) => {
                    const price = pickPrice(a.prices, interval) ?? pickPrice(a.prices, 'monthly');
                    return (_jsxs("div", { style: {
                            background: theme.surface,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 4,
                            padding: 20,
                            minWidth: 0,
                        }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }, children: [_jsx("h3", { style: {
                                            ...serif,
                                            fontSize: 20,
                                            lineHeight: 1.1,
                                            margin: 0,
                                            color: theme.text,
                                            letterSpacing: -0.5,
                                        }, children: a.name }), showDraftBadges && a.status === 'draft' ? (_jsx("span", { style: badge(theme, 'draft'), "aria-label": "Draft (not live)", children: "Draft" })) : null] }), a.description !== null && a.description.length > 0 ? (_jsx("p", { style: { ...sans, fontSize: 13, color: theme.body, margin: '0 0 10px' }, children: a.description })) : null, price !== undefined ? (_jsxs("div", { style: { ...sans, fontSize: 15, color: theme.text }, children: [formatPrice(price), _jsxs("span", { style: { color: theme.muted }, children: [' ', "\u00B7 ", a.scope === 'account' ? 'per account' : 'per site'] })] })) : null] }, a.addon_id));
                }) })] }));
}
//# sourceMappingURL=PricingTable.js.map